import React from "react";
import { useState, useEffect, useCallback } from "react";
import { api } from "../../api/client";
import { Loader } from "../Loader";
import { GENDER_BG_COLORS } from "../../pages/AdminDashboard";
import { getCurrentDateInKarachi } from "../../utils/dateUtils";

const TripRecorded = () => {
  const [stops, setStops] = useState([]);
  const [recordedTrips, setRecordedTrips] = useState([]);
  const [recordedTripsLoading, setRecordedTripsLoading] = useState(false);
  const [selectedRecordedTripId, setSelectedRecordedTripId] = useState(null);
  const [startDate, setStartDate] = useState(getCurrentDateInKarachi());
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);

  const fetchRecordedTrips = async (page = 1) => {
    setRecordedTripsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const r = await api(`/admin/trips/recorded?${params.toString()}`);
      setRecordedTrips(r.recordedTrips);
      setTotalPages(r.totalPages);
      setTotalCount(r.totalCount);
      setCurrentPage(page);
    } catch (err) {
      alert(err.message || "Failed to load recorded trips");
    }
    setRecordedTripsLoading(false);
  };

  const deleteRecordedTrip = async (tripId) => {
    if (!window.confirm("Are you sure you want to delete this recorded trip?")) {
      return;
    }

    try {
      await api(`/admin/trips/recorded/${tripId}`, { method: "DELETE" });
      alert("Recorded trip deleted successfully");
      fetchRecordedTrips();
      setSelectedRecordedTripId(null);
    } catch (err) {
      alert(err.message || "Failed to delete recorded trip");
    }
  };

  const load = async () => {
    const stopsRes = await api("/admin/stops");
    setStops(stopsRes.stops);
    fetchRecordedTrips();
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (stops.length > 0) {
      fetchRecordedTrips(1);
    }
  }, [startDate, endDate]);

  return (
    <div>
      <h4>Trips Record</h4>
      
      <div style={{ 
        marginBottom: "1rem",
        padding: "1rem",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
      }}>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
          gap: "1rem",
          marginBottom: "1rem"
        }}>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600 }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || getCurrentDateInKarachi()}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600 }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={getCurrentDateInKarachi()}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            className="btn"
            onClick={() => {
              fetchRecordedTrips(1);
              setSelectedRecordedTripId(null);
            }}
          >
            Refresh
          </button>
          <button
            className="btn secondary"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </button>
          <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
            {startDate && endDate ? (
              <>Showing trips from <strong>{startDate}</strong> to <strong>{endDate}</strong></>
            ) : startDate ? (
              <>Showing trips from <strong>{startDate}</strong> onwards</>
            ) : endDate ? (
              <>Showing trips till <strong>{endDate}</strong></>
            ) : (
              <>Showing all trips</>
            )}
          </span>
        </div>
      </div>

      {!selectedRecordedTripId && !recordedTripsLoading && recordedTrips.length > 0 && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.5rem",
          marginTop: "1.5rem",
          padding: "1rem",
        }}>
          <button
            className="btn"
            onClick={() => fetchRecordedTrips(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              opacity: currentPage === 1 ? 0.5 : 1,
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: "0.875rem", color: "#374151" }}>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> 
          </span>
          <button
            className="btn"
            onClick={() => fetchRecordedTrips(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              opacity: currentPage === totalPages ? 0.5 : 1,
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}

      {recordedTripsLoading ? (
        <Loader />
      ) : recordedTrips.length === 0 ? (
        <p>No recorded trips found</p>
      ) : selectedRecordedTripId ? (
        (() => {
          const trip = recordedTrips.find(
            (t) => t._id === selectedRecordedTripId
          );
          if (!trip) return <p>Trip not found</p>;

          const d = new Date(trip.date);
          const formatted = d.toLocaleDateString("en-GB", {
            weekday: "long",
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          return (
            <div>
              <div style={{ marginBottom: 16, display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn"
                  onClick={() => setSelectedRecordedTripId(null)}
                >
                  ← Back to Recorded Trips
                </button>
                <button
                  className="btn"
                  style={{ background: "#dc2626", color: "white" }}
                  onClick={() => deleteRecordedTrip(trip._id)}
                >
                  Delete Trip Record
                </button>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  background: "#fff",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    marginBottom: 8,
                    fontSize: "0.9rem",
                    color: "#6b7280",
                  }}
                >
                  {formatted}
                </p>
                <h3 style={{ margin: 0, marginBottom: 16 }}>
                  {trip.type.charAt(0).toUpperCase() + trip.type.slice(1)} Trip ({trip.minTime} - {trip.maxTime})
                </h3>

                {trip.cars.map((carEntry, carIdx) => {
                  const sortedPassengers = [...carEntry.passengers].sort(
                    (a, b) => {
                      const stopA = stops.find(
                        (s) => s.name === (a.stop?.name || a.stop)
                      );
                      const stopB = stops.find(
                        (s) => s.name === (b.stop?.name || b.stop)
                      );
                      const orderA = stopA ? stopA.order : 999;
                      const orderB = stopB ? stopB.order : 999;
                      return trip.type === "morning"
                        ? orderA - orderB
                        : orderB - orderA;
                    }
                  );

                  return (
                    <div key={carIdx} style={{ marginBottom: "2rem" }}>
                      <h4 style={{ marginBottom: "0.5rem" }}>
                        {carEntry.carName} {carEntry.carNumber}
                      </h4>

                      {sortedPassengers.length > carEntry.carCapacity && (
                        <div
                          style={{
                            padding: "0.5rem",
                            background: "#fef2f2",
                            border: "1px solid #ef4444",
                            borderRadius: 4,
                            color: "#b91c1c",
                            fontSize: "0.875rem",
                            marginBottom: 8,
                          }}
                        >
                          <strong>Warning:</strong> Passenger count (
                          {sortedPassengers.length}) exceeds car capacity (
                          {carEntry.carCapacity})
                        </div>
                      )}

                      <div className="scroll-x">
                        <table className="table table-list">
                          <thead>
                            <tr>
                              <th>Sr #</th>
                              <th>Passenger Name</th>
                              <th>Stop/Address</th>
                              <th>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedPassengers.length === 0 && (
                              <tr>
                                <td colSpan={4}>No passengers</td>
                              </tr>
                            )}
                            {sortedPassengers.map((p, idx) => {
                              const sched = p.schedule?.find(
                                (s) => s.day === trip.day
                              );
                              const passengerTime = `${
                                sched?.morning || "-"
                              } - ${sched?.evening || "-"}`;
                              const passengerBgColor =
                                GENDER_BG_COLORS[p.gender] || "transparent";

                              return (
                                <tr key={p._id}>
                                  <td>{idx + 1}</td>
                                  <td
                                    style={{
                                      backgroundColor: passengerBgColor,
                                    }}
                                  >
                                    {p.name}
                                  </td>
                                  <td>{p.stop?.name || p.stop || "-"}</td>
                                  <td>{passengerTime}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()
      ) : (
        <div
          style={{
            display: "grid",
            gap: "1rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          }}
        >
          {recordedTrips.map((trip) => {
            const d = new Date(trip.date);
            const formatted = d.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
            const totalPassengers = trip.cars.reduce(
              (sum, c) => sum + c.passengers.length,
              0
            );

            return (
              <div
                key={trip._id}
                onClick={() => setSelectedRecordedTripId(trip._id)}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "1rem",
                  background: "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  borderTop: "4px solid #2d7a64",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    marginBottom: 4,
                  }}
                >
                  {formatted}
                </div>
                <h4 style={{ margin: 0, marginBottom: 8 }}>
                  {trip.type.charAt(0).toUpperCase() + trip.type.slice(1)} Trip ({trip.minTime} - {trip.maxTime})
                </h4>
                <div style={{ fontSize: "0.875rem", color: "#374151" }}>
                  {trip.cars.map((c, idx) => (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      <strong>
                        {c.carName} {c.carNumber}:
                      </strong>{" "}
                      {c.passengers.length} passenger
                      {c.passengers.length !== 1 ? "s" : ""}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#2d7a64",
                  }}
                >
                  Total: {totalPassengers} passenger
                  {totalPassengers !== 1 ? "s" : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {!selectedRecordedTripId && !recordedTripsLoading && recordedTrips.length > 0 && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.5rem",
          marginTop: "1.5rem",
          padding: "1rem",
        }}>
          <button
            className="btn"
            onClick={() => fetchRecordedTrips(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              opacity: currentPage === 1 ? 0.5 : 1,
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: "0.875rem", color: "#374151" }}>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> 
          </span>
          <button
            className="btn"
            onClick={() => fetchRecordedTrips(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              opacity: currentPage === totalPages ? 0.5 : 1,
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TripRecorded;
